import React, { useState } from 'react';
import { Phone, Fan as Fax, Mail, MapPin, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const MiningContact: React.FC<{ content?: any }> = ({ content }) => {
  const { t, t_api } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [message, setMessage] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    } else if (!/^[a-zA-Z\u0600-\u06FF\s'-]{2,}$/.test(formData.name.trim())) {
      newErrors.name = "Name must contain only letters (min 2 characters).";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required.";
    } else if (formData.subject.trim().length < 3) {
      newErrors.subject = "Subject must be at least 3 characters.";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setStatus(null);
    setMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, division: "mining" }),
      });

      if (response.ok) {
        setStatus("success");
        setMessage("✅ Your message has been sent successfully!");
        setFormData({ name: "", email: "", subject: "", message: "" });
        setErrors({});

        setTimeout(() => {
          setStatus(null);
          setMessage("");
        }, 3000);
      } else {
        setStatus("error");
        setMessage("❌ Failed to send message. Try again later.");
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("error");
      setMessage("❌ Error sending message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white py-10">
    <div className="max-w-7xl mx-auto px-4">
      <motion.div
        className="bg-white rounded-3xl shadow-sm border overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Contact Form */}
          <div className="p-8 md:p-10">
            <motion.h2
              className="text-3xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {t_api(content?.formTitle || "Send Us Email", content?.formTitle_ar || "أرسل لنا البريد الإلكتروني")}
            </motion.h2>

            {loading && (
              <div className="mb-4 flex items-center gap-2 bg-blue-100 border border-blue-300 text-blue-800 px-4 py-3 rounded-md">
                <Loader2 className="animate-spin h-5 w-5" />
                <span>{t('common.loading')}</span>
              </div>
            )}

            {status === "success" && (
              <div className="mb-4 flex items-center gap-2 bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded-md">
                <CheckCircle className="h-5 w-5" />
                <span>{message}</span>
              </div>
            )}

            {status === "error" && (
              <div className="mb-4 flex items-center gap-2 bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-md">
                <AlertCircle className="h-5 w-5" />
                <span>{message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('contact.name')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.name ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('contact.email')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.email ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.subject')} <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.subject ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                />
                {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject}</p>}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.message')} <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.message ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                ></textarea>
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
              </div>

              <button
                type="submit"
                className="bg-red-600 text-white px-6 py-3 rounded-md font-medium hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <Send className="h-4 w-4" />
                <span>{t('contact.send')}</span>
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <motion.div
            className="bg-gray-50 p-8 md:p-10"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-2 mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t_api(content?.infoTitle || "Get in Touch", content?.infoTitle_ar || "تواصل معنا")}</h2>
            </div>

            <p className="text-gray-700 text-sm md:text-base text-justify mb-6">
              {t_api(content?.infoDesc || "We appreciate your interest in Hamdan Mining, and we want to make it as easy as possible for you to get your queries answered.", content?.infoDesc_ar)}
            </p>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 font-cambria">{t('contact.info')}</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-red-600 mb-3 text-[15px]">{t_api("Hamdan Mining", "حمدان للتعدين")}</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-blue-600 shrink-0" />
                        <span className="text-gray-700 text-[14px] leading-relaxed">
                          {t_api(content?.address || "Salalah, Sultanate of Oman", content?.address_ar || "صلالة، سلطنة عمان")}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-green-600 shrink-0" />
                        <span className="text-gray-700 text-[14px]">
                          {content?.phone || "+968 23-210000"}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Fax className="h-5 w-5 text-purple-600 shrink-0" />
                        <span className="text-gray-700 text-[14px]">
                          Fax: {content?.fax || "+(968) 23212666"}
                        </span>
                      </div>
                      {content?.email && (
                        <div className="flex items-center space-x-3">
                          <Mail className="h-5 w-5 text-red-500 shrink-0" />
                          <a href={`mailto:${content.email}`} className="text-blue-600 hover:text-blue-700 underline text-[14px]">
                            {content.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        <div className="h-1 w-full bg-gradient-to-r from-red-700 via-rose-500 to-yellow-400" />
      </motion.div>
    </div>
    </section>
  );
};

export default MiningContact;
